import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import {
  $accountPaymentListStore,
  $reportStore,
  $accountPaymentReportStore,
  $paymentStore,
  $profileFeatureStore,
  $initialCurrenciesAvailableStore,
} from '@lib/store';
import { useListState } from '@shared/hooks';
import { cn, FormatUtils, LocalStorageUtils, PaletteUtils } from '@shared/utils';
import {
  AccountType,
  PaymentReportColumnsEnum,
  ReportExportFileType,
  type AccountResponse,
  type GeneratePaymentReportRequest,
  type GetDepositPaymentsByAccountsRequest,
} from '@shared/api';
import { ReportSecondaryTabs } from '../../models';
import { DepositAccountTable, DepositTransactionTable } from './';
import { ColumnsModal } from '../columnsModal';
import { GeneratedFileModal } from '../generatedFileModal';
import { Button, Icon, Modal, Radio, SearchInput, Select, SelectDateRange, Spinner, Tabs } from '@shared/widgets';
import DownloadIcon from '@assets/icons/download.svg';
import PlusIcon from '@assets/icons/plus.svg';

const ColumnsEnum = {
  Status: 'Status',
  ReferenceNumber: 'ReferenceNumber',
  Date: 'Date',
  AccountNumber: 'AccountNumber',
  Direction: 'Direction',
  TypeOfTransfer: 'TypeOfTransfer',
  TransactionReference: 'TransactionReference',
  SenderIban: 'SenderIban',
  BeneficiaryIban: 'BeneficiaryIban',
  ExchangeRate: 'ExchangeRate',
  SenderAmount: 'SenderAmount',
  BeneficiaryAmount: 'BeneficiaryAmount',
  Fee: 'Fee',
  TotalAmount: 'TotalAmount',
} as const;
type ColumnsEnum = (typeof ColumnsEnum)[keyof typeof ColumnsEnum];

const COLUMNS = [
  ColumnsEnum.Status,
  ColumnsEnum.ReferenceNumber,
  ColumnsEnum.Date,
  ColumnsEnum.AccountNumber,
  ColumnsEnum.Direction,
  ColumnsEnum.TypeOfTransfer,
  ColumnsEnum.TransactionReference,
  ColumnsEnum.SenderIban,
  ColumnsEnum.BeneficiaryIban,
  ColumnsEnum.ExchangeRate,
  ColumnsEnum.SenderAmount,
  ColumnsEnum.BeneficiaryAmount,
  ColumnsEnum.Fee,
  ColumnsEnum.TotalAmount,
];

const mapColumnToEnum = (column: string): PaymentReportColumnsEnum | undefined => {
  const columns: { [key: string]: PaymentReportColumnsEnum } = {
    [ColumnsEnum.Status]: PaymentReportColumnsEnum.Status,
    [ColumnsEnum.ReferenceNumber]: PaymentReportColumnsEnum.ReferenceNumber,
    [ColumnsEnum.Date]: PaymentReportColumnsEnum.Date,
    [ColumnsEnum.AccountNumber]: PaymentReportColumnsEnum.AccountNumber,
    [ColumnsEnum.Direction]: PaymentReportColumnsEnum.Direction,
    [ColumnsEnum.TypeOfTransfer]: PaymentReportColumnsEnum.TypeOfTransfer,
    [ColumnsEnum.TransactionReference]: PaymentReportColumnsEnum.TransactionReference,
    [ColumnsEnum.SenderIban]: PaymentReportColumnsEnum.SenderIban,
    [ColumnsEnum.BeneficiaryIban]: PaymentReportColumnsEnum.BeneficiaryIban,
    [ColumnsEnum.ExchangeRate]: PaymentReportColumnsEnum.ExchangeRate,
    [ColumnsEnum.SenderAmount]: PaymentReportColumnsEnum.SenderAmount,
    [ColumnsEnum.BeneficiaryAmount]: PaymentReportColumnsEnum.BeneficiaryAmount,
    [ColumnsEnum.Fee]: PaymentReportColumnsEnum.Fee,
    [ColumnsEnum.TotalAmount]: PaymentReportColumnsEnum.TotalAmount,
  };

  return columns[column];
};

export const ReportDepositTab = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const paymentStore = useUnit($paymentStore.store);
  const accountsStore = useUnit($accountPaymentListStore.store);
  const accountReportStore = useUnit($accountPaymentReportStore.store);
  const reportStore = useUnit($reportStore.store);

  const [tab, setTab] = useState<ReportSecondaryTabs>(ReportSecondaryTabs.Transactions);
  const [searchText, setSearchText] = useState('');
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);
  const isTransactionTab = tab === ReportSecondaryTabs.Transactions;

  const columns = LocalStorageUtils.getPaymentTransactionColumns();
  const [isOpenFieldsModal, setIsOpenFieldsModal] = useState<boolean>(false);
  const [transactionColumns, setTransactionColumns] = useState<string[]>(columns || COLUMNS);
  const updateColumns = (activeColumns: string[]) => {
    setTransactionColumns(activeColumns);
    LocalStorageUtils.setPaymentTransactionColumns(activeColumns);
  };

  const changeTabHandler = (tab: ReportSecondaryTabs) => {
    setTab(tab);
    setCurrency(undefined);
  };

  const [currency, setCurrency] = useState<string>();
  const [account, setAccount] = useState<AccountResponse | undefined>(undefined);
  const [dateRange, setDateRange] = useState<{ dateFrom?: Dayjs; dateTo?: Dayjs } | undefined>(undefined);
  const [isOpenExportModal, setIsOpenExportModal] = useState(false);

  const reportsPaymentsTransactionsState = useListState({ list: 'reportsPaymentsTransactions' });
  const reportsPaymentsAccountsState = useListState({ list: 'reportsPaymentsAccounts' });

  const { t } = useTranslation('reports');

  const updateAccounts = () => {
    $accountPaymentReportStore.events.getPaymentAccountBalanceReport({
      Currency: currency,
      ReturnTotalCount: true,
      Count: reportsPaymentsAccountsState.count,
      FromIndex: reportsPaymentsAccountsState.fromIndex,
    });
  };

  const updateTransactions = () => {
    if (accountsStore.data.length <= 0) return;
    $paymentStore.events.getPaymentsByAccounts({
      AccountIds: account ? [account.id] : accountsStore.data.map((x) => x.id),
      Currency: currency,
      DateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      DateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      SearchText: searchText,
      ReturnTotalCount: true,
      Count: reportsPaymentsTransactionsState.count,
      FromIndex: reportsPaymentsTransactionsState.fromIndex,
    });
  };

  const onExport = (item: ReportExportFileType) => {
    setIsOpenExportModal(false);
    if (isTransactionTab) {
      const columnsVisible = transactionColumns.map(mapColumnToEnum).filter((col) => !!col);
      const request: GeneratePaymentReportRequest = {
        reportType: item,
        columnsVisible,
        accountIds: account ? [account.id] : accountsStore.data.map((x) => x.id),
        currency: currency,
        searchText: searchText,
        dateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        dateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      };
      $reportStore.events.generatePaymentReport(request);
    } else {
      $reportStore.events.generatePaymentAccountBalanceReport({
        ReportType: item,
        Currency: currency,
      });
    }
  };

  useEffect(() => {
    if (!reportStore.loading && reportStore.data) setShowGeneratedModal(true);
  }, [reportStore.data, reportStore.loading]);

  useEffect(() => {
    if (tab !== ReportSecondaryTabs.AccountsBalance) return;
    if (!profileFeatureStore.data?.reports?.viewAccountBalanceReport) return;
    updateAccounts();
  }, [tab, currency, reportsPaymentsAccountsState]);

  useEffect(() => {
    if (tab !== ReportSecondaryTabs.Transactions) return;
    updateTransactions();
  }, [tab, currency, searchText, account, dateRange, reportsPaymentsTransactionsState]);

  useEffect(() => {
    if (accountsStore.data.length <= 0) return;
    const request: GetDepositPaymentsByAccountsRequest = {
      AccountIds: accountsStore.data.filter((x) => x.type === AccountType.Payment).map((x) => x.id),
    };
    if (request.AccountIds.length <= 0) return;
    $paymentStore.events.getPaymentsByAccounts(request);
  }, [accountsStore]);

  useEffect(() => {
    $accountPaymentListStore.events.getAccounts({ AccountType: AccountType.Payment });
    if (profileFeatureStore.data?.reports?.viewAccountBalanceReport) $accountPaymentReportStore.events.getPaymentAccountBalanceReport({ Count: 100 });

    return () => {
      $accountPaymentReportStore.events.clear();
      $accountPaymentListStore.events.clear();
      $paymentStore.events.clear();
    };
  }, []);

  const tabOptions = useMemo(
    () =>
      Object.entries(ReportSecondaryTabs)
        .map(([, value]) => ({ label: t(value), value }))
        .filter(
          (tab) =>
            tab.value === ReportSecondaryTabs.Transactions ||
            (profileFeatureStore.data?.reports?.viewAccountBalanceReport && tab.value === ReportSecondaryTabs.AccountsBalance),
        ),
    [t, profileFeatureStore.data?.reports?.viewAccountBalanceReport],
  );
  const exportOptions = useMemo(() => Object.entries(ReportExportFileType).map(([value, label]) => ({ text: t(label), value })), [t]);
  return (
    <div className='flex h-full w-full flex-col'>
      <div className='my-4 flex items-center gap-2'>
        {isTransactionTab && <SearchInput withDebounce maxLength={128} value={searchText} onChange={setSearchText} />}
        <div className='ml-auto flex gap-2'>
          {isTransactionTab && (
            <Button color='primary' onClick={() => setIsOpenFieldsModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('buttons.addFilter')}
            </Button>
          )}
          <Button
            color='primary'
            disabled={isTransactionTab ? !paymentStore.data.data.length : !accountReportStore.data.data.length}
            onClick={() => setIsOpenExportModal(true)}
          >
            <Icon icon={<DownloadIcon />} color='inherit' />
            {t('buttons.export')}
          </Button>
        </div>
      </div>
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && 'max-md:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName='h-full gap-0'
          labelClassName={cn('p-4', 'hover:bg-white dark:hover:bg-soft-black')}
          activeLabelClassName='bg-white dark:bg-soft-black'
          options={tabOptions}
          hideIndicator
          value={tab}
          onChange={changeTabHandler}
        />
        <div className={cn('my-auto mb-6 flex', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-2')}>
          <Select
            className='min-w-[160px]'
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
          {isTransactionTab && (
            <>
              <Select
                className={cn('ml-6', PaletteUtils.isVcfcPalette && 'max-md:ml-0')}
                items={accountsStore.data.map((x) => ({ label: FormatUtils.formatAccountNumber(x.accountNumber) || '', value: x }))}
                nullValue={{ isEnable: true, text: t('placeholders.allAccounts', { ns: 'common' }) }}
                value={account}
                onChange={setAccount}
              />
              <SelectDateRange className={cn('ml-6', PaletteUtils.isVcfcPalette && 'max-md:ml-0')} value={dateRange} onChange={setDateRange} />
            </>
          )}
        </div>
      </div>
      <div className={cn('flex w-full', PaletteUtils.isVcfcPalette && 'flex-col rounded-b-xl bg-white p-6 dark:bg-soft-black')}>
        {profileFeatureStore.data?.reports?.viewAccountBalanceReport && tab === ReportSecondaryTabs.AccountsBalance && (
          <DepositAccountTable
            isLoading={accountReportStore.loading}
            error={accountReportStore.error}
            onRefresh={updateAccounts}
            accounts={accountReportStore.data.data}
            totalItemCount={accountReportStore.data.total}
            currentPage={reportsPaymentsAccountsState.page}
            countItemPage={reportsPaymentsAccountsState.count}
            onChangePage={reportsPaymentsAccountsState.onChangePage}
            onChangeCountItemPage={reportsPaymentsAccountsState.onChangeCount}
          />
        )}
        {profileFeatureStore.data?.reports?.viewTransactions && tab === ReportSecondaryTabs.Transactions && (
          <DepositTransactionTable
            isLoading={paymentStore.loading || accountsStore.loading}
            error={paymentStore.error}
            onRefresh={updateTransactions}
            columns={transactionColumns}
            items={paymentStore.data.data}
            totalItemCount={paymentStore.data.total}
            currentPage={reportsPaymentsTransactionsState.page}
            countItemPage={reportsPaymentsTransactionsState.count}
            onChangePage={reportsPaymentsTransactionsState.onChangePage}
            onChangeCountItemPage={reportsPaymentsTransactionsState.onChangeCount}
          />
        )}
      </div>
      <Modal isOpen={isOpenExportModal} onClose={() => setIsOpenExportModal(false)} title={t('modals.download.title')}>
        <div className='flex flex-col'>
          <Radio orientation='vertical' options={exportOptions} onChange={(val) => onExport(val as ReportExportFileType)} />
        </div>
      </Modal>
      {reportStore.loading && <Spinner position />}
      <ColumnsModal
        show={isOpenFieldsModal}
        onClose={() => setIsOpenFieldsModal(false)}
        columns={COLUMNS}
        activeColumns={transactionColumns}
        onSave={updateColumns}
      />
      <GeneratedFileModal open={showGeneratedModal} onClose={() => setShowGeneratedModal(false)} />
    </div>
  );
};
